
import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface AdaptiveKnowledgeGraphProps {
  topics: string[];
  content: string;
}

interface GraphNode {
  id: string;
  label: string;
  group?: string;
  value?: number;
}

interface GraphLink {
  source: string;
  target: string;
  value?: number;
}

export const AdaptiveKnowledgeGraph: React.FC<AdaptiveKnowledgeGraphProps> = ({ topics, content }) => {
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState<{ nodes: GraphNode[], links: GraphLink[] } | null>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  
  useEffect(() => {
    const generateKnowledgeGraph = async () => {
      setLoading(true);
      try {
        // Use AI to analyze connections between topics
        const { data, error } = await supabase.functions.invoke('ai-learning-assistant', {
          body: {
            action: 'generate_knowledge_graph',
            topics,
            content
          }
        });
        
        if (error) throw error;
        
        if (data?.graph) {
          setGraphData(data.graph);
        } else {
          // Fallback to simple graph if AI doesn't return expected format
          const fallbackGraph = generateFallbackGraph(topics);
          setGraphData(fallbackGraph);
        }
      } catch (error) {
        console.error('Error generating knowledge graph:', error);
        toast.error('Failed to generate knowledge graph');
        
        // Always show something even on error
        const fallbackGraph = generateFallbackGraph(topics);
        setGraphData(fallbackGraph);
      } finally {
        setLoading(false);
      }
    };
    
    generateKnowledgeGraph();
  }, [topics, content]);
  
  const generateFallbackGraph = (topics: string[]) => {
    // Create a simple graph structure as fallback
    const mainTopic = 'Current Affairs';
    
    const nodes: GraphNode[] = [
      { id: mainTopic, label: mainTopic, group: 'main', value: 10 },
      ...topics.map((topic, i) => ({
        id: topic,
        label: topic,
        group: 'topic',
        value: 5
      }))
    ];
    
    const links: GraphLink[] = topics.map(topic => ({
      source: mainTopic,
      target: topic,
      value: 1
    }));
    
    // Add some connections between topics for more interesting visualization
    for (let i = 0; i < topics.length - 1; i++) {
      if (Math.random() > 0.5) { // Only connect some topics randomly
        links.push({
          source: topics[i],
          target: topics[i + 1],
          value: 1
        });
      }
    }
    
    return { nodes, links };
  };
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 2.5));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };
  
  const resetZoom = () => {
    setZoom(1);
  };
  
  // Generate a simple SVG representation of the graph
  const renderSimpleSVGGraph = () => {
    if (!graphData || !graphData.nodes.length) return null;
    
    const width = 600;
    const height = 400;
    const nodeRadius = 20;
    
    // Position nodes in a circular layout
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 3;
    
    // Position main node at center
    let mainNode = graphData.nodes.find(n => n.group === 'main');
    if (!mainNode && graphData.nodes.length > 0) {
      mainNode = graphData.nodes[0];
    }
    
    const nodePositions: Record<string, {x: number, y: number}> = {};
    
    if (mainNode) {
      nodePositions[mainNode.id] = { x: centerX, y: centerY };
    }
    
    // Position other nodes in a circle around the main node
    const otherNodes = graphData.nodes.filter(n => n !== mainNode);
    otherNodes.forEach((node, i) => {
      const angle = (i / otherNodes.length) * Math.PI * 2;
      nodePositions[node.id] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      };
    });
    
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {/* Draw links first so they appear behind nodes */}
        {graphData.links.map((link, i) => {
          const source = nodePositions[link.source];
          const target = nodePositions[link.target];
          
          if (!source || !target) return null;
          
          return (
            <line
              key={`link-${i}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke="#aaa"
              strokeWidth="2"
            />
          );
        })}
        
        {/* Draw nodes */}
        {graphData.nodes.map((node, i) => {
          const pos = nodePositions[node.id];
          if (!pos) return null;
          
          const isMainNode = node.group === 'main';
          
          return (
            <g key={`node-${i}`}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={isMainNode ? nodeRadius * 1.5 : nodeRadius}
                fill={isMainNode ? '#3b82f6' : '#60a5fa'}
                stroke="#fff"
                strokeWidth="2"
              />
              <text
                x={pos.x}
                y={pos.y + 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                fontSize={isMainNode ? "12px" : "10px"}
                fontWeight={isMainNode ? "bold" : "normal"}
              >
                {node.label.substring(0, 10)}{node.label.length > 10 ? '...' : ''}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };
  
  return (
    <Card className="bg-white">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Knowledge Graph</h3>
          <div className="flex gap-2">
            <Button size="icon" variant="outline" onClick={handleZoomOut}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" onClick={resetZoom}>
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" onClick={handleZoomIn}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div 
          ref={graphContainerRef}
          className="border rounded-lg bg-slate-50 h-[400px] overflow-hidden relative"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease'
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : graphData ? (
            renderSimpleSVGGraph()
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">No graph data available</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 text-sm text-muted-foreground">
          <p>This knowledge graph shows the relationships between key current affairs topics and how they connect to your learning areas.</p>
        </div>
      </CardContent>
    </Card>
  );
};
